/** Phase 6 on the web: the Adventure Builder, adventure templates (incl. pre-built one-shots) and the campaign's adventure. */
import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  cleanAdventureContent,
  type Adventure, type AdventureContent, type AdventureSummary, type AdventuresResponse, type CampaignAdventureResponse, type CampaignSummary,
} from "@dm/shared";
import { api } from "./api.js";
import { useApp } from "./appContext.js";
import { useGame } from "./game.js";

const levels = (a: { levelMin: number; levelMax: number }) => (a.levelMin === a.levelMax ? `Level ${a.levelMin}` : `Levels ${a.levelMin}–${a.levelMax}`);
const lines = (s: string) => s.split("\n").map((x) => x.trim()).filter(Boolean);

// ------------------------------------------------------------------ list + builder

export function AdventuresPage({ id }: { id?: number }) {
  const { me, fail } = useApp();
  const [data, setData] = useState<AdventuresResponse | null>(null);
  const load = useCallback(() => api.adventures().then(setData, fail), [fail]);
  useEffect(() => void load(), [load]);
  if (id) return <AdventureEditor id={id} onChanged={load} />;
  if (!data) return <main className="muted">Loading…</main>;
  const groups: Array<[string, AdventureSummary[]]> = [
    ["Pre-built one-shots", data.adventures.filter((a) => a.prebuilt)],
    ["Yours", data.adventures.filter((a) => !a.prebuilt && a.ownerId === me.user.discordId)],
    ["Shared by other Hosts", data.adventures.filter((a) => !a.prebuilt && a.ownerId !== me.user.discordId)],
  ];
  return (
    <main className="adventures">
      {data.canRead ? <Builder /> : <p className="panel muted">Adventures hold the DM's secrets, so only Hosts can open them. Ask your Host what's next!</p>}
      {groups.map(([label, list]) => list.length > 0 && (
        <section className="panel" key={label}>
          <h3>{label}</h3>
          <div className="adventure-cards">
            {list.map((a) => (
              <a key={a.id} className="adventure-card" href={data.canRead || a.ownerId === me.user.discordId ? `#/adventures/${a.id}` : undefined}>
                <strong>{a.title}</strong>
                <span className="muted small">{levels(a)} · {a.length}{a.shared && !a.prebuilt ? " · shared" : ""}{a.ownerName && !a.prebuilt ? ` · by ${a.ownerName}` : ""}</span>
                <span className="small">{a.pitch || <em className="muted">No pitch yet.</em>}</span>
              </a>
            ))}
          </div>
        </section>
      ))}
    </main>
  );
}

function Builder() {
  const { fail } = useApp();
  const [mine, setMine] = useState<CampaignSummary[]>([]);
  const [idea, setIdea] = useState("");
  const [campaignId, setCampaignId] = useState<number | null>(null);
  const [lo, setLo] = useState(1);
  const [hi, setHi] = useState(3);
  const [length, setLength] = useState<"one-shot" | "short" | "long">("one-shot");
  const [tone, setTone] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.campaigns().then((r) => {
      const runs = r.mine.filter((c) => c.role === "host" || c.role === "cohost");
      setMine(runs);
      setCampaignId((cur) => cur ?? r.selectedId ?? runs[0]?.campaign.id ?? null);
    }, fail);
  }, [fail]);
  const build = async () => {
    if (!campaignId) return;
    setBusy(true);
    try {
      const a = await api.buildAdventure({ idea, campaignId, levelMin: lo, levelMax: hi, length, tone });
      window.location.hash = `#/adventures/${a.id}`;
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  const blank = async () => {
    try {
      const a = await api.createAdventure("New adventure", { acts: [{ title: "Act 1", summary: "", goal: "", scenes: [] }] });
      window.location.hash = `#/adventures/${a.id}`;
    } catch (err) {
      fail(err);
    }
  };
  const runs = mine.filter((c) => c.campaign.id === campaignId);
  return (
    <section className="panel builder">
      <h3>Adventure Builder</h3>
      <label htmlFor="adv-idea">Your idea</label>
      <textarea id="adv-idea" rows={3} value={idea} maxLength={2000} placeholder="e.g. A haunted lighthouse whose keeper has been dead for years, but the light still turns every night…" onChange={(e) => setIdea(e.target.value)} />
      <div className="row wrap">
        <label>Levels <input type="number" min={1} max={20} value={lo} onChange={(e) => setLo(Number(e.target.value))} style={{ width: "4em" }} /> to{" "}
          <input type="number" min={1} max={20} value={hi} onChange={(e) => setHi(Number(e.target.value))} style={{ width: "4em" }} /></label>
        <label>Length <select value={length} onChange={(e) => setLength(e.target.value as typeof length)}>
          <option value="one-shot">One-shot (3 acts)</option><option value="short">Short (4 acts)</option><option value="long">Longer (5 acts)</option>
        </select></label>
        <label>Tone <input value={tone} maxLength={120} placeholder="spooky, funny, epic…" onChange={(e) => setTone(e.target.value)} /></label>
      </div>
      <div className="row wrap">
        <label>Paid by <select value={campaignId ?? ""} onChange={(e) => setCampaignId(Number(e.target.value))}>
          {!mine.length && <option value="">— host a campaign first —</option>}
          {mine.map((c) => <option key={c.campaign.id} value={c.campaign.id}>{c.campaign.name}</option>)}
        </select></label>
        <button className="primary" disabled={busy || idea.trim().length < 10 || !campaignId} onClick={build}>{busy ? "Grok is writing… (up to a minute)" : "Build with Grok"}</button>
        <button onClick={blank} disabled={busy}>Start a blank one</button>
      </div>
      <p className="muted small">
        Grok writes acts, NPCs, places, encounters, loot and secrets; you can edit everything afterwards. It runs on {runs[0] ? `“${runs[0].campaign.name}”'s` : "the chosen campaign's"} AI key and shows up on its Costs tab as "Adventure Builder".
      </p>
    </section>
  );
}

// ------------------------------------------------------------------ editor

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="field"><span>{label}</span>{children}</label>;
}

function ListEditor<T>({ title, items, blank, render, onChange, max }: {
  title: string; items: T[]; blank: T; max: number; onChange: (v: T[]) => void; render: (item: T, set: (v: T) => void) => ReactNode;
}) {
  return (
    <section className="panel">
      <h3>{title} <span className="muted small">({items.length})</span></h3>
      {items.map((it, i) => (
        <div key={i} className="list-item">
          {render(it, (v) => onChange(items.map((x, j) => (j === i ? v : x))))}
          <div className="row">
            {i > 0 && <button className="link" onClick={() => onChange(items.map((x, j) => (j === i - 1 ? items[i]! : j === i ? items[i - 1]! : x)))}>↑ Up</button>}
            <button className="link danger" onClick={() => onChange(items.filter((_, j) => j !== i))}>Remove</button>
          </div>
        </div>
      ))}
      {items.length < max && <button onClick={() => onChange([...items, blank])}>+ Add</button>}
    </section>
  );
}

export function AdventureEditor({ id, onChanged }: { id: number; onChanged: () => void }) {
  const { me, fail } = useApp();
  const [a, setA] = useState<Adventure | null>(null);
  const [title, setTitle] = useState("");
  const [c, setC] = useState<AdventureContent | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  useEffect(() => {
    api.adventure(id).then((x) => {
      setA(x);
      setTitle(x.title);
      setC(x.content);
      setDirty(false);
    }, fail);
  }, [id, fail]);
  if (!a || !c) return <main className="muted">Loading…</main>;
  const ro = !a.canEdit;
  const set = (patch: Partial<AdventureContent>) => {
    setC({ ...c, ...patch });
    setDirty(true);
    setSaved(null);
  };
  const save = async (extra: { shared?: boolean } = {}) => {
    try {
      const x = await api.saveAdventure(a.id, { title, content: cleanAdventureContent(c), ...extra });
      setA(x);
      setC(x.content);
      setDirty(false);
      setSaved("Saved");
      onChanged();
    } catch (err) {
      fail(err);
    }
  };
  const copy = async () => {
    try {
      const x = await api.copyAdventure(a.id);
      onChanged();
      window.location.hash = `#/adventures/${x.id}`;
    } catch (err) {
      fail(err);
    }
  };
  const del = async () => {
    if (!window.confirm(`Delete “${a.title}”? Campaigns already playing it keep their copy.`)) return;
    try {
      await api.deleteAdventure(a.id);
      onChanged();
      window.location.hash = "#/adventures";
    } catch (err) {
      fail(err);
    }
  };
  const text = (v: string, on: (s: string) => void, rows = 2, max = 3000) =>
    rows > 1 ? <textarea rows={rows} value={v} maxLength={max} disabled={ro} onChange={(e) => on(e.target.value)} /> : <input value={v} maxLength={max} disabled={ro} onChange={(e) => on(e.target.value)} />;
  return (
    <main className="adventure-editor">
      <section className="panel">
        <h3>
          <a href="#/adventures" className="small">← Adventures</a>
          {a.prebuilt && <span className="tag">Pre-built</span>}
          {a.shared && !a.prebuilt && <span className="tag">Shared</span>}
        </h3>
        <Field label="Title">{text(title, (v) => { setTitle(v); setDirty(true); }, 1, 120)}</Field>
        <Field label="Pitch (read to the players; no spoilers)">{text(c.pitch, (v) => set({ pitch: v }), 3, 1200)}</Field>
        <Field label="What's really going on (DM only)">{text(c.overview, (v) => set({ overview: v }), 4, 4000)}</Field>
        <div className="row wrap">
          <Field label="Levels"><span>
            <input type="number" min={1} max={20} value={c.levelMin} disabled={ro} style={{ width: "4em" }} onChange={(e) => set({ levelMin: Number(e.target.value) })} /> to{" "}
            <input type="number" min={1} max={20} value={c.levelMax} disabled={ro} style={{ width: "4em" }} onChange={(e) => set({ levelMax: Number(e.target.value) })} />
          </span></Field>
          <Field label="Length">{text(c.length, (v) => set({ length: v }), 1, 60)}</Field>
          <Field label="Tone">{text(c.tone, (v) => set({ tone: v }), 1, 120)}</Field>
        </div>
        <div className="row wrap">
          {!ro && <button className="primary" disabled={!dirty} onClick={() => save()}>Save</button>}
          {saved && !dirty && <span className="muted small">{saved}</span>}
          {!ro && !a.prebuilt && (
            <label className="small"><input type="checkbox" checked={a.shared} onChange={(e) => save({ shared: e.target.checked })} /> Share with other Hosts</label>
          )}
          <button onClick={copy}>Make my own copy</button>
          {!ro && <button className="danger" onClick={del}>Delete</button>}
          <PlayHere adventure={a} />
          {ro && <span className="muted small">{a.prebuilt ? (me.isAdmin ? "" : "Pre-built: make a copy to change it.") : "Read-only: make a copy to change it."}</span>}
        </div>
      </section>
      <ListEditor title="Acts" items={c.acts} max={8} blank={{ title: "", summary: "", goal: "", scenes: [] }} onChange={(acts) => set({ acts })}
        render={(x, s) => (<>
          <Field label="Title">{text(x.title, (v) => s({ ...x, title: v }), 1, 120)}</Field>
          <Field label="Goal (when it's done, the DM moves on)">{text(x.goal, (v) => s({ ...x, goal: v }), 1, 500)}</Field>
          <Field label="What happens (DM view)">{text(x.summary, (v) => s({ ...x, summary: v }), 4)}</Field>
          <Field label="Scene ideas (one per line)">
            <textarea rows={3} disabled={ro} value={x.scenes.join("\n")} onChange={(e) => s({ ...x, scenes: e.target.value.split("\n") })} onBlur={() => s({ ...x, scenes: lines(x.scenes.join("\n")) })} />
          </Field>
          <Field label="Key beats (one per line; each must happen before the DM can move on, however the players get there)">
            <textarea rows={3} disabled={ro} value={(x.beats ?? []).join("\n")} onChange={(e) => s({ ...x, beats: e.target.value.split("\n") })} onBlur={() => s({ ...x, beats: lines((x.beats ?? []).join("\n")) })} />
          </Field>
        </>)} />
      <ListEditor title="NPCs" items={c.npcs} max={20} blank={{ name: "", role: "", description: "", secret: "", voiceHint: "" }} onChange={(npcs) => set({ npcs })}
        render={(x, s) => (<>
          <div className="row wrap">
            <Field label="Name">{text(x.name, (v) => s({ ...x, name: v }), 1, 80)}</Field>
            <Field label="Role">{text(x.role, (v) => s({ ...x, role: v }), 1, 120)}</Field>
            <Field label="Voice">{text(x.voiceHint, (v) => s({ ...x, voiceHint: v }), 1, 200)}</Field>
          </div>
          <Field label="Description">{text(x.description, (v) => s({ ...x, description: v }), 2, 1200)}</Field>
          <Field label="Secret (DM only)">{text(x.secret, (v) => s({ ...x, secret: v }), 1, 800)}</Field>
        </>)} />
      <ListEditor title="Places" items={c.locations} max={20} blank={{ name: "", description: "", secret: "" }} onChange={(locations) => set({ locations })}
        render={(x, s) => (<>
          <Field label="Name">{text(x.name, (v) => s({ ...x, name: v }), 1, 80)}</Field>
          <Field label="Description">{text(x.description, (v) => s({ ...x, description: v }), 2, 1200)}</Field>
          <Field label="Secret (DM only)">{text(x.secret, (v) => s({ ...x, secret: v }), 1, 800)}</Field>
        </>)} />
      <ListEditor title="Encounters" items={c.encounters} max={24} blank={{ name: "", act: 1, monsters: "", difficulty: "medium" as const, tactics: "" }} onChange={(encounters) => set({ encounters })}
        render={(x, s) => (<>
          <div className="row wrap">
            <Field label="Name">{text(x.name, (v) => s({ ...x, name: v }), 1, 120)}</Field>
            <Field label="Act"><select value={x.act} disabled={ro} onChange={(e) => s({ ...x, act: Number(e.target.value) })}>
              <option value={0}>Any</option>{c.acts.map((_, i) => <option key={i} value={i + 1}>{i + 1}</option>)}
            </select></Field>
            <Field label="Difficulty"><select value={x.difficulty} disabled={ro} onChange={(e) => s({ ...x, difficulty: e.target.value as typeof x.difficulty })}>
              {["easy", "medium", "hard", "deadly"].map((d) => <option key={d}>{d}</option>)}
            </select></Field>
          </div>
          <Field label="Monsters / challenge">{text(x.monsters, (v) => s({ ...x, monsters: v }), 1, 600)}</Field>
          <Field label="Tactics">{text(x.tactics, (v) => s({ ...x, tactics: v }), 2, 800)}</Field>
        </>)} />
      <ListEditor title="Loot" items={c.loot} max={24} blank={{ name: "", where: "", notes: "" }} onChange={(loot) => set({ loot })}
        render={(x, s) => (
          <div className="row wrap">
            <Field label="Item">{text(x.name, (v) => s({ ...x, name: v }), 1, 120)}</Field>
            <Field label="Where">{text(x.where, (v) => s({ ...x, where: v }), 1, 300)}</Field>
            <Field label="Notes">{text(x.notes, (v) => s({ ...x, notes: v }), 1, 600)}</Field>
          </div>
        )} />
      <section className="panel">
        <h3>Secrets and ending</h3>
        <Field label="Secrets and twists (one per line; the DM lets the players discover them)">
          <textarea rows={4} disabled={ro} value={c.secrets.join("\n")} onChange={(e) => set({ secrets: e.target.value.split("\n") })} />
        </Field>
        <Field label="How the players can find out each secret (one line per secret, same order: clues, NPCs, checks)">
          <textarea rows={4} disabled={ro} value={(c.revealRoutes ?? []).join("\n")} onChange={(e) => set({ revealRoutes: e.target.value.split("\n") })} />
        </Field>
        <Field label="End-of-adventure checklist (one per line: rewards, loot, the villain's fate)">
          <textarea rows={4} disabled={ro} value={(c.wrapUp ?? []).join("\n")} onChange={(e) => set({ wrapUp: e.target.value.split("\n") })} />
        </Field>
        <Field label="How it can end">{text(c.ending, (v) => set({ ending: v }), 3, 2000)}</Field>
        {!ro && <button className="primary" disabled={!dirty} onClick={() => save()}>Save</button>}
      </section>
    </main>
  );
}

/** "Play in <campaign>" for Hosts / Co-hosts of the selected campaign. */
function PlayHere({ adventure }: { adventure: Adventure }) {
  const { me, fail } = useApp();
  const staff = me.campaign && (me.role === "host" || me.role === "cohost" || me.isAdmin);
  if (!staff || !me.campaign) return null;
  const c = me.campaign;
  const play = async () => {
    if (!window.confirm(`Play “${adventure.title}” in ${c.name}? Its NPCs and places are added to the campaign's memory (hidden from players until met). A running adventure is marked finished.`)) return;
    try {
      await api.startAdventure(c.id, adventure.id);
      window.location.hash = "#/host";
    } catch (err) {
      fail(err);
    }
  };
  return <button className="primary" onClick={play}>Play in {c.name}</button>;
}

// ------------------------------------------------------------------ in the campaign

/** Host panel: the running adventure, act by act. */
export function HostAdventure() {
  const { campaign, fail, adventureVersion } = useGame();
  const [data, setData] = useState<CampaignAdventureResponse | null>(null);
  const [list, setList] = useState<AdventureSummary[]>([]);
  const [pick, setPick] = useState<number | "">("");
  const [suggesting, setSuggesting] = useState(false);
  const load = useCallback(() => api.campaignAdventure(campaign.id).then(setData, fail), [campaign.id, fail]);
  useEffect(() => void load(), [load, adventureVersion]);
  useEffect(() => void api.adventures().then((r) => setList(r.adventures), () => undefined), []);
  if (!data) return null;
  const cur = data.current;
  const acts = cur?.content?.acts ?? [];
  const go = (fn: () => Promise<CampaignAdventureResponse>) => fn().then(setData, fail);
  // Older adventures (from before key beats) have acts with none: offer to have Grok write them.
  const missingBeats = cur ? acts.filter((a, i) => i >= cur.currentAct && !(a.beats ?? []).some((b) => b.trim())).length : 0;
  const suggest = async () => {
    setSuggesting(true);
    try {
      setData(await api.suggestBeats(campaign.id));
    } catch (err) {
      fail(err);
    } finally {
      setSuggesting(false);
    }
  };
  return (
    <section className="panel host-adventure">
      <h3>Adventure {cur && <span className="muted small">{cur.title}</span>}</h3>
      {cur ? (
        <>
          <ol className="acts">
            {acts.map((a, i) => (
              <li key={i} className={i === cur.currentAct ? "current" : i < cur.currentAct ? "done" : ""}>
                <strong>{a.title}</strong>{i === cur.currentAct && <span className="tag ok-tag">now</span>}
                {i === cur.currentAct && <div className="small">Goal: {a.goal || "—"}<br /><span className="muted">{a.summary}</span></div>}
                {i === cur.currentAct && (a.beats ?? []).length > 0 && (
                  <ul className="beats small">
                    {(a.beats ?? []).map((b, n) => (
                      <li key={n}>
                        <label>
                          <input type="checkbox" checked={(cur.beatsDone ?? []).includes(n)}
                            onChange={(e) => go(() => api.patchAdventure(campaign.id, { beat: n, beatDone: e.target.checked }))} /> {b}
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ol>
          {missingBeats > 0 && (
            <div className="notice small beats-offer">
              {missingBeats === 1 ? "1 act has" : `${missingBeats} acts have`} no key beats (this adventure predates them), so nothing stops the DM from rushing
              {missingBeats === 1 ? " it" : " them"}.{" "}
              <button disabled={suggesting} onClick={() => void suggest()}>{suggesting ? "Writing key beats…" : "Suggest key beats"}</button>
              <span className="muted"> Grok writes 2-4 for each such act into this game's copy (a small AI cost on the campaign key). Tick or untick them like any other beat.</span>
            </div>
          )}
          <div className="row wrap">
            <button disabled={cur.currentAct === 0} onClick={() => go(() => api.patchAdventure(campaign.id, { currentAct: cur.currentAct - 1 }))}>← Previous act</button>
            <button disabled={cur.currentAct >= acts.length - 1} onClick={() => go(() => api.patchAdventure(campaign.id, { currentAct: cur.currentAct + 1 }))}>Next act →</button>
            <button onClick={() => window.confirm(`Finish “${cur.title}”? The DM stops running it.`) && go(() => api.finishAdventure(campaign.id))}>Finish adventure</button>
            {cur.adventureId && <a className="small" href={`#/adventures/${cur.adventureId}`}>Open the template</a>}
          </div>
          <p className="muted small">The DM follows the current act and moves on by itself when the party reaches its goal and every key beat has happened (ticked above; the DM ticks them as they happen). "Next act" moves on anyway, or tell the DM to move on. Players only see the pitch and the acts reached so far.</p>
        </>
      ) : (
        <p className="muted small">No adventure running: the DM improvises from the campaign's memory. Pick one to give it a story to run.</p>
      )}
      <div className="row wrap">
        <select value={pick} onChange={(e) => setPick(e.target.value ? Number(e.target.value) : "")} aria-label="Adventure to play">
          <option value="">{cur ? "Switch to another adventure…" : "Choose an adventure…"}</option>
          {list.map((a) => <option key={a.id} value={a.id}>{a.title}{a.prebuilt ? " (pre-built)" : ""} · {levels(a)}</option>)}
        </select>
        <button className="primary" disabled={pick === ""} onClick={() => pick !== "" && window.confirm("Start this adventure? Its NPCs and places are added to memory (hidden from players until met).") && go(() => api.startAdventure(campaign.id, pick)).then(() => setPick(""))}>Start</button>
        <a className="small" href="#/adventures">Browse or build adventures</a>
      </div>
    </section>
  );
}

/** Table page: what the players know about the adventure. */
export function AdventureBanner() {
  const { campaign, adventureVersion } = useGame();
  const [data, setData] = useState<CampaignAdventureResponse | null>(null);
  useEffect(() => void api.campaignAdventure(campaign.id).then(setData, () => undefined), [campaign.id, adventureVersion]);
  const cur = data?.current;
  if (!cur) return null;
  return (
    <details className="panel adventure-banner">
      <summary><strong>{cur.title}</strong> <span className="muted small">· {cur.actTitles.at(-1)}</span></summary>
      {cur.pitch && <p className="small">{cur.pitch}</p>}
      <ol className="small">{cur.actTitles.map((t, i) => <li key={i}>{t}</li>)}</ol>
    </details>
  );
}
